import axios from 'axios';
import {
  PRESET_THEMES,
  applyThemeToDOM,
  getStoredTheme,
  saveStoredTheme,
  extractThemeConfig,
} from '../../utils/themeHelper';

export default {
  namespaced: true,
  state: () => ({
    themeId: 'rhinos-gold',
    currentTheme: JSON.parse(JSON.stringify(PRESET_THEMES['rhinos-gold'])),
    presets: PRESET_THEMES,
    isSaving: false,
  }),
  getters: {
    currentTheme(state) {
      return state.currentTheme;
    },
    themeId(state) {
      return state.themeId;
    },
    themeColors(state) {
      return state.currentTheme.colors || {};
    },
    themeBackground(state) {
      return state.currentTheme.background || {};
    },
    themePresets(state) {
      return state.presets;
    },
    primaryColor(state) {
      return state.currentTheme.colors?.primary || '#DAA520';
    },
    navBarBg(state) {
      return state.currentTheme.colors?.navBar || state.currentTheme.colors?.primary || '#DAA520';
    },
    navBarText(state) {
      return state.currentTheme.colors?.navText || '#000000';
    },
    drawerBg(state) {
      return state.currentTheme.colors?.drawerBg || '#212121';
    },
    cardBg(state) {
      return state.currentTheme.colors?.cardBg || 'rgba(33, 33, 33, 0.85)';
    },
    backgroundColor(state) {
      return state.currentTheme.colors?.background || '#000000';
    },
    isSaving(state) {
      return state.isSaving;
    },
  },
  mutations: {
    INIT_THEME(state, themeConfig) {
      if (themeConfig) {
        state.currentTheme = JSON.parse(JSON.stringify(themeConfig));
        state.themeId = themeConfig.id || 'custom';
      }
    },
    SET_PRESET_THEME(state, presetId) {
      const preset = state.presets[presetId];
      if (preset) {
        state.themeId = presetId;
        state.currentTheme = JSON.parse(JSON.stringify(preset));
      }
    },
    SET_CUSTOM_COLOR(state, { key, value }) {
      if (!state.currentTheme.colors) {
        state.currentTheme.colors = {};
      }
      state.currentTheme.colors[key] = value;
      state.themeId = 'custom';
      state.currentTheme.id = 'custom';
    },
    SET_BACKGROUND(state, bgPayload) {
      if (!state.currentTheme.background) {
        state.currentTheme.background = {
          mode: 'video',
          presetId: 'rhinos_gold_dark',
          customUrl: '',
          overlayOpacity: 0.3,
        };
      }
      state.currentTheme.background = {
        ...state.currentTheme.background,
        ...bgPayload,
      };
      if (
        state.currentTheme.background.mode === 'preset' &&
        !state.currentTheme.background.presetId
      ) {
        state.currentTheme.background.presetId = 'rhinos_gold_dark';
      }
      state.themeId = 'custom';
      state.currentTheme.id = 'custom';
    },
    SET_FULL_THEME(state, themeConfig) {
      if (themeConfig && themeConfig.colors) {
        state.currentTheme = JSON.parse(JSON.stringify(themeConfig));
        state.themeId = themeConfig.id || 'custom';
      }
    },
    SET_SAVING(state, isSaving) {
      state.isSaving = !!isSaving;
    },
  },
  actions: {
    initTheme({ commit }) {
      const stored = getStoredTheme();
      commit('INIT_THEME', stored);
      applyThemeToDOM(stored);
    },
    async applyPreset({ commit, state, dispatch }, presetId) {
      commit('SET_PRESET_THEME', presetId);
      applyThemeToDOM(state.currentTheme);
      saveStoredTheme(state.currentTheme);
      await dispatch('debouncedSave');
    },
    async updateCustomColor({ commit, state, dispatch }, payload) {
      commit('SET_CUSTOM_COLOR', payload);
      applyThemeToDOM(state.currentTheme);
      saveStoredTheme(state.currentTheme);
      await dispatch('debouncedSave');
    },
    async updateBackground({ commit, state, dispatch }, payload) {
      commit('SET_BACKGROUND', payload);
      applyThemeToDOM(state.currentTheme);
      saveStoredTheme(state.currentTheme);
      await dispatch('debouncedSave');
    },
    debouncedSave({ dispatch }) {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        dispatch('saveThemeToBackend');
      }, 1000);
    },
    syncThemeFromBackend({ commit }, backendData) {
      if (!backendData) return;
      const config = extractThemeConfig(backendData);
      if (config && config.colors) {
        commit('SET_FULL_THEME', config);
        applyThemeToDOM(config);
        saveStoredTheme(config);
      }
    },
    async saveThemeToBackend({ commit, state, rootGetters }) {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }
      const iboId = rootGetters.iboId;
      const isAuthenticated = rootGetters.userIsAuthenticated;
      if (!iboId || !isAuthenticated) {
        return { success: true, localOnly: true };
      }
      commit('SET_SAVING', true);
      try {
        const payload = {
          token: process.env.VUE_APP_WEB_TOKEN,
          updateType: '6',
          themeSettings: state.currentTheme,
        };
        const response = await axios.put(
          `${process.env.VUE_APP_API_URL}ibos/${iboId}`,
          payload,
          {
            headers: { 'Content-Type': 'application/json' },
            responseType: 'json',
          }
        );
        commit('SET_SAVING', false);
        if (response.data && response.data.success) {
          saveStoredTheme(state.currentTheme);
        }
        return response.data;
      } catch (error) {
        commit('SET_SAVING', false);
        console.warn('Could not sync theme with backend:', error);
        return { success: false, error };
      }
    },
    async resetToDefault({ dispatch }) {
      return dispatch('applyPreset', 'rhinos-gold');
    },
  },
};
let debounceTimer = null;
